// ---------------------------------------------------------
// Datei: templateCacheStore.ts
// Zweck: Persistenter Offline-Cache für die aggregierten Projektvorbereitung-/Projektdokumentation
//        (VOT)-Templates (Ticket #1055, Nutzer-Rückmeldung: "es funktioniert offline nicht").
// Kontext: Anders als dealsStore/documentState hatten diese beiden Endpunkte (jeweils ein
//          One360-Backend-Response, der HubSpot+Cosmos+SQL+Drive+OpsSteuerzentrale kombiniert)
//          bisher KEINE Persistenz — jedes Öffnen der Phase lud sie live neu, ohne Fallback bei
//          fehlender Verbindung. Folgt demselben Zustand-persist-Muster wie dealsStore.ts
//          (localStorage-Storage mit Quota-sicherem Fallback auf In-Memory), statt localforage zu
//          nutzen (das ist in diesem Projekt bisher nur für die Upload-Queue reserviert).
// Stand: Fetch bleibt bei bestehender Internetverbindung weiterhin "network-first" (Daten können
//        sich serverseitig ändern) — der Cache greift nur als Fallback, wenn `navigator.onLine`
//        false ist oder der Live-Request fehlschlägt, s. Verwendung in ProjektvorbereitungBriefing/
//        ProjektdokumentationWizard.
// ---------------------------------------------------------

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ProjektvorbereitungTemplate } from '../interfaces/projektvorbereitung';
import type { ProjektdokumentationTemplate } from '../types/projektdokumentation';

function isQuotaExceededError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, code, message } = error as { name?: unknown; code?: unknown; message?: unknown };
  const normalizedMessage = String(message || '').toLowerCase();
  return (
    name === 'QuotaExceededError' ||
    name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    code === 22 ||
    code === 1014 ||
    normalizedMessage.includes('quota') ||
    normalizedMessage.includes('exceeded')
  );
}

// Dieselbe Absicherung wie dealsStore.ts's createSafeStorage — bewusst hier dupliziert statt
// gemeinsam exportiert, da dealsStore.ts nicht als geteiltes Utility-Modul angelegt ist.
function createSafeStorage() {
  const memoryStorage = new Map<string, string>();
  const safeMemory = {
    getItem: (key: string) => memoryStorage.get(key) ?? null,
    setItem: (key: string, value: string) => {
      memoryStorage.set(key, value);
    },
    removeItem: (key: string) => {
      memoryStorage.delete(key);
    },
  };

  if (typeof window === 'undefined' || !window.localStorage) {
    return safeMemory;
  }

  return {
    getItem: (key: string) => {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return memoryStorage.get(key) ?? null;
      }
    },
    setItem: (key: string, value: string) => {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        if (isQuotaExceededError(error)) {
          console.warn('[templateCacheStore] localStorage quota exceeded, skipping persist write.');
          return;
        }
        console.warn('[templateCacheStore] localStorage write failed, skipping persist write.', error);
      }
    },
    removeItem: (key: string) => {
      try {
        window.localStorage.removeItem(key);
      } catch {
        memoryStorage.delete(key);
      }
    },
  };
}

interface TemplateCacheState {
  projektvorbereitung: Record<string, ProjektvorbereitungTemplate>;
  projektdokumentation: Record<string, ProjektdokumentationTemplate>;
  setProjektvorbereitungTemplate: (projectId: string, template: ProjektvorbereitungTemplate) => void;
  setProjektdokumentationTemplate: (projectId: string, template: ProjektdokumentationTemplate) => void;
  // Security-Review-Finding P1: dieser Cache persistiert account-unabhängig in localStorage
  // (wie dealsStore) und enthält Kundenadressen, Kontakte, Ops-Kommentare und VOT-Daten pro
  // projectId. Ohne diese Aktion würde er Logout/401/Kontowechsel überleben — auf einem
  // gemeinsam genutzten Gerät könnte das nächste Konto bei Offline-/API-Fehler dann Daten des
  // vorherigen Kontos aus dem Fallback sehen. Muss an denselben drei Stellen wie
  // useDealsStore.clearDeals() aufgerufen werden (AuthContext: logout, auth:unauthorized,
  // auth:wrong-account).
  clearAll: () => void;
}

export const useTemplateCacheStore = create<TemplateCacheState>()(
  persist(
    (set) => ({
      projektvorbereitung: {},
      projektdokumentation: {},
      setProjektvorbereitungTemplate: (projectId, template) =>
        set((state) => ({
          projektvorbereitung: { ...state.projektvorbereitung, [projectId]: template },
        })),
      setProjektdokumentationTemplate: (projectId, template) =>
        set((state) => ({
          projektdokumentation: { ...state.projektdokumentation, [projectId]: template },
        })),
      clearAll: () => set({ projektvorbereitung: {}, projektdokumentation: {} }),
    }),
    {
      name: '360volt-template-cache-store',
      storage: createJSONStorage(createSafeStorage),
    }
  )
);
